import { useEffect, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { 
  LayoutGrid, Wallet, History, PieChart, Newspaper, Settings, Bell,
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownLeft, Search,
  ChevronRight, ExternalLink, Clock, Shield, Star, Users,
  CreditCard, Building2, Globe, Smartphone, Copy, CheckCircle,
  BarChart3, ArrowRight, Zap, Target, Award, Calendar
} from "lucide-react";

const RATES = { BTC: 6800000, ETH: 250000, USDT: 76, RUB: 1 };
const fmt = (n, d = 2) => parseFloat(n || 0).toLocaleString('ru-RU', { minimumFractionDigits: d, maximumFractionDigits: d });
const toRub = (a, c) => parseFloat(a || 0) * (RATES[c] || 1);

// Fake news data
const NEWS = [
  { id: 1, title: "Bitcoin достиг нового максимума в 2026 году", time: "2 часа назад", category: "BTC", hot: true },
  { id: 2, title: "Ethereum 2.0: что изменилось после обновления", time: "5 часов назад", category: "ETH", hot: false },
  { id: 3, title: "Регуляторы ЕС одобрили новые правила для криптобирж", time: "8 часов назад", category: "Регуляции", hot: true },
  { id: 4, title: "Аналитики прогнозируют рост USDT в марте", time: "12 часов назад", category: "USDT", hot: false },
  { id: 5, title: "Топ-5 криптовалют для инвестиций в 2026", time: "1 день назад", category: "Обзор", hot: false },
];

export default function CryptoTradeCombinedPreview() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [page, setPage] = useState('dashboard');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const d = searchParams.get('data');
    const p = searchParams.get('page');
    
    // Check URL path for page type (e.g., /templates/cryptotrade/settings)
    const pathParts = location.pathname.split('/');
    const pathPage = pathParts[pathParts.length - 1];
    
    // Map path/query to internal page names
    const pageMap = { 
      main: 'dashboard', 
      history: 'history', 
      settings: 'profile',
      profile: 'profile',
      combined: 'dashboard'
    };
    
    // Priority: query param > URL path
    if (p) {
      setPage(pageMap[p] || p);
    } else if (pathPage && pageMap[pathPage]) {
      setPage(pageMap[pathPage]);
    }
    
    if (d) {
      try {
        const parsed = JSON.parse(decodeURIComponent(d));
        setData(parsed);
        if (parsed.transactions?.length) {
          const dates = parsed.transactions.filter(t => t.date).map(t => {
            const parts = t.date.split('.');
            return parts.length === 3 ? { o: t.date, s: +parts[2] * 10000 + +parts[1] * 100 + +parts[0] } : null;
          }).filter(Boolean).sort((a, b) => a.s - b.s);
          if (dates.length) { setDateFrom(dates[0].o); setDateTo(dates[dates.length - 1].o); }
        }
      } catch (e) {}
    }
  }, [searchParams, location.pathname]);

  useEffect(() => { document.title = 'CoinVault Pro — Криптоплатформа'; }, []);

  if (!data) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  const name = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Пользователь';
  const uid = data.visibleId || Math.floor(100000 + Math.random() * 900000);
  
  const coins = [
    { sym: 'BTC', name: 'Bitcoin', bal: data.btcBalance || '0', color: '#f7931a', change: 2.4 },
    { sym: 'ETH', name: 'Ethereum', bal: data.ethBalance || '0', color: '#627eea', change: -0.8 },
    { sym: 'USDT', name: 'Tether', bal: data.usdtBalance || '0', color: '#26a17b', change: 0.1 },
    { sym: 'RUB', name: 'Рубли', bal: data.balance || '0', color: '#6366f1', change: 0 },
  ];
  
  const total = coins.reduce((s, c) => s + toRub(c.bal, c.sym), 0);

  // === TOP NAVIGATION ===
  const TopNav = () => (
    <header className="h-16 bg-white border-b border-slate-200 px-6 flex items-center justify-between sticky top-0 z-50">
      <div className="flex items-center gap-8">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-xl text-slate-800">CoinVault</span>
          <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-600">PRO</span>
        </div>

        {/* Main Nav */}
        <nav className="flex items-center gap-1">
          {[
            { id: 'dashboard', label: 'Обзор', icon: LayoutGrid },
            { id: 'portfolio', label: 'Портфель', icon: PieChart },
            { id: 'history', label: 'История', icon: History },
            { id: 'news', label: 'Новости', icon: Newspaper },
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setPage(item.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                page === item.id 
                  ? 'bg-blue-50 text-blue-600' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="flex items-center gap-4">
        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input 
            placeholder="Поиск..." 
            className="pl-10 pr-4 py-2 rounded-lg text-sm bg-slate-50 border border-slate-200 w-64 focus:outline-none focus:border-blue-300"
          />
        </div>

        {/* Notifications */}
        <button className="relative p-2 rounded-lg hover:bg-slate-50">
          <Bell className="w-5 h-5 text-slate-600" />
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500"></span>
        </button>

        {/* Profile */}
        <button onClick={() => setPage('profile')} className="flex items-center gap-3 pl-4 border-l border-slate-200">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-semibold text-sm">
            {name.charAt(0)}
          </div>
          <div className="text-left">
            <div className="text-sm font-medium text-slate-800">{name}</div>
            <div className="text-xs text-slate-500">ID: {uid}</div>
          </div>
        </button>
      </div>
    </header>
  );

  // === SIDEBAR ===
  const Sidebar = () => (
    <aside className="w-64 bg-white border-r border-slate-200 p-4 flex flex-col">
      {/* Balance Card */}
      <div className="rounded-2xl p-5 mb-6 bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
        <div className="text-sm opacity-80 mb-1">Общий баланс</div>
        <div className="text-2xl font-bold mb-2">{fmt(total, 0)} ₽</div>
        <div className="flex items-center gap-1 text-sm opacity-80">
          <TrendingUp className="w-4 h-4" />
          <span>+{fmt(total * 0.08, 0)} ₽ (8%)</span>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-6">
        <div className="text-xs font-semibold text-slate-400 uppercase mb-3">Быстрые действия</div>
        <div className="space-y-2">
          {[
            { icon: ArrowUpRight, label: 'Вывести', color: 'text-orange-500 bg-orange-50' },
            { icon: ArrowDownLeft, label: 'Пополнить', color: 'text-green-500 bg-green-50' },
            { icon: BarChart3, label: 'Обменять', color: 'text-blue-500 bg-blue-50' },
          ].map((action, i) => (
            <button key={i} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${action.color}`}>
                <action.icon className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium text-slate-700">{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Assets */}
      <div className="flex-1">
        <div className="text-xs font-semibold text-slate-400 uppercase mb-3">Мои активы</div>
        <div className="space-y-2">
          {coins.filter(c => parseFloat(c.bal) > 0).map((c, i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 cursor-pointer transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: c.color }}>
                  {c.sym.charAt(0)}
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-800">{c.sym}</div>
                  <div className="text-xs text-slate-500">{c.name}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-slate-800">{c.sym === 'RUB' ? fmt(c.bal, 0) : c.bal}</div>
                <div className={`text-xs ${c.change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {c.change >= 0 ? '+' : ''}{c.change}%
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Support */}
      <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-5 h-5 text-blue-500" />
          <span className="text-sm font-medium text-slate-800">Поддержка 24/7</span>
        </div>
        <div className="text-xs text-slate-500">Нужна помощь? Мы онлайн</div>
      </div>
    </aside>
  );

  // === DASHBOARD PAGE ===
  const DashboardPage = () => (
    <div className="flex-1 p-6 overflow-auto">
      <div className="grid grid-cols-3 gap-6">
        {/* Main Content - 2 columns */}
        <div className="col-span-2 space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Портфель', value: `${fmt(total, 0)} ₽`, change: '+8.4%', icon: Wallet, color: 'blue' },
              { label: 'Прибыль', value: `+${fmt(total * 0.08, 0)} ₽`, change: 'за месяц', icon: TrendingUp, color: 'green' },
              { label: 'Операций', value: data.transactions?.length || 0, change: 'всего', icon: History, color: 'purple' },
            ].map((stat, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-${stat.color}-50`}>
                    <stat.icon className={`w-5 h-5 text-${stat.color}-500`} />
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full bg-${stat.color}-50 text-${stat.color}-600`}>{stat.change}</span>
                </div>
                <div className="text-2xl font-bold text-slate-800 mb-1">{stat.value}</div>
                <div className="text-sm text-slate-500">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Recent Transactions */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Последние операции</h3>
              <button onClick={() => setPage('history')} className="text-sm text-blue-500 flex items-center gap-1 hover:text-blue-600">
                Все операции <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="divide-y divide-slate-100">
              {(data.transactions || []).slice(0, 5).map((tx, i) => {
                // withdrawal = Зачисление (incoming), deposit = Вывод средств (outgoing)
                const isIncoming = tx.type === 'withdrawal';
                return (
                  <div key={i} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isIncoming ? 'bg-green-50' : 'bg-orange-50'}`}>
                        {isIncoming ? <ArrowDownLeft className="w-5 h-5 text-green-500" /> : <ArrowUpRight className="w-5 h-5 text-orange-500" />}
                      </div>
                      <div>
                        <div className="font-medium text-slate-800">{isIncoming ? 'Зачисление' : 'Вывод средств'}</div>
                        <div className="text-sm text-slate-500">{tx.date} в {tx.time}</div>
                      </div>
                    </div>
                    <div className={`font-semibold ${isIncoming ? 'text-green-500' : 'text-orange-500'}`}>
                      {isIncoming ? '+' : '-'}{fmt(tx.amount, 0)} ₽
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* News Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <Newspaper className="w-5 h-5 text-blue-500" />
                Новости рынка
              </h3>
              <button onClick={() => setPage('news')} className="text-sm text-blue-500 hover:text-blue-600">Все</button>
            </div>
            <div className="divide-y divide-slate-100">
              {NEWS.slice(0, 4).map((news) => (
                <div key={news.id} className="p-4 hover:bg-slate-50 cursor-pointer transition-colors">
                  <div className="flex items-start gap-3">
                    {news.hot && <Zap className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />}
                    <div>
                      <div className="text-sm font-medium text-slate-800 mb-1 line-clamp-2">{news.title}</div>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="px-2 py-0.5 rounded bg-slate-100">{news.category}</span>
                        <span>{news.time}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Promo Card */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-100">
            <Award className="w-8 h-8 text-amber-500 mb-3" />
            <div className="font-semibold text-slate-800 mb-1">Программа лояльности</div>
            <div className="text-sm text-slate-600 mb-3">Получайте до 5% кэшбэка с каждой операции</div>
            <button className="text-sm font-medium text-amber-600 flex items-center gap-1 hover:text-amber-700">
              Подробнее <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // === PORTFOLIO PAGE ===
  const PortfolioPage = () => (
    <div className="flex-1 p-6 overflow-auto">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">Инвестиционный портфель</h1>
      
      <div className="grid grid-cols-4 gap-4 mb-6">
        {coins.filter(c => parseFloat(c.bal) > 0).map((c, i) => {
          const value = toRub(c.bal, c.sym);
          const pct = total > 0 ? (value / total * 100).toFixed(1) : 0;
          return (
            <div key={i} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold" style={{ backgroundColor: c.color }}>
                  {c.sym.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-slate-800">{c.name}</div>
                  <div className="text-sm text-slate-500">{c.sym}</div>
                </div>
              </div>
              <div className="text-xl font-bold text-slate-800 mb-1">
                {c.sym === 'RUB' ? fmt(c.bal, 0) : c.bal} {c.sym === 'RUB' ? '₽' : c.sym}
              </div>
              <div className="text-sm text-slate-500 mb-3">≈ {fmt(value, 0)} ₽</div>
              <div className="flex items-center justify-between">
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div className="h-2 rounded-full" style={{ width: `${pct}%`, backgroundColor: c.color }}></div>
                </div>
                <span className="ml-3 text-sm font-medium text-slate-600">{pct}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // === HISTORY PAGE ===
  const HistoryPage = () => {
    const parse = d => { const p = d?.split('.'); return p?.length === 3 ? +p[2] * 10000 + +p[1] * 100 + +p[0] : 0; };
    let txs = [...(data.transactions || [])].sort((a, b) => parse(a.date) - parse(b.date));
    if (dateFrom || dateTo) {
      const f = dateFrom ? parse(dateFrom) : 0, t = dateTo ? parse(dateTo) : 99999999;
      txs = txs.filter(x => { const d = parse(x.date); return d >= f && d <= t; });
    }

    return (
      <div className="flex-1 p-6 overflow-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-slate-800">История операций</h1>
          <div className="flex items-center gap-3 bg-white rounded-xl px-4 py-2 border border-slate-200">
            <Calendar className="w-5 h-5 text-slate-400" />
            <input value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="С даты" className="w-28 text-sm outline-none" />
            <span className="text-slate-400">—</span>
            <input value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="По дату" className="w-28 text-sm outline-none" />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="divide-y divide-slate-100">
            {txs.map((tx, i) => {
              // withdrawal = Зачисление (incoming), deposit = Вывод средств (outgoing)
              const isIncoming = tx.type === 'withdrawal';
              return (
                <div key={i} className="p-5 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isIncoming ? 'bg-green-50' : 'bg-orange-50'}`}>
                      {isIncoming ? <ArrowDownLeft className="w-6 h-6 text-green-500" /> : <ArrowUpRight className="w-6 h-6 text-orange-500" />}
                    </div>
                    <div>
                      <div className="font-medium text-slate-800">{isIncoming ? 'Зачисление' : 'Вывод средств'}</div>
                      <div className="text-sm text-slate-500">{tx.currency || 'RUB'} • {tx.date}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-semibold ${isIncoming ? 'text-green-500' : 'text-orange-500'}`}>
                      {isIncoming ? '+' : '-'}{fmt(tx.amount, 0)} ₽
                    </div>
                    <div className="text-sm text-slate-500">{tx.time}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // === NEWS PAGE ===
  const NewsPage = () => (
    <div className="flex-1 p-6 overflow-auto">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">Новости криптовалют</h1>
      
      <div className="grid grid-cols-2 gap-6">
        {NEWS.map((news) => (
          <div key={news.id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
            <div className="flex items-center gap-2 mb-3">
              {news.hot && <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-500">🔥 Горячее</span>}
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">{news.category}</span>
            </div>
            <h3 className="font-semibold text-slate-800 text-lg mb-2">{news.title}</h3>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Clock className="w-4 h-4" />
              {news.time}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // === PROFILE PAGE ===
  const ProfilePage = () => (
    <div className="flex-1 p-6 overflow-auto">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">Мой профиль</h1>
      
      <div className="grid grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <div className="text-center mb-6">
            <div className="w-24 h-24 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-4xl font-bold text-white">
              {name.charAt(0)}
            </div>
            <h2 className="text-xl font-bold text-slate-800">{name}</h2>
            <div className="flex items-center justify-center gap-2 mt-1 text-slate-500">
              <span>ID: {uid}</span>
              <button><Copy className="w-4 h-4 text-blue-500" /></button>
            </div>
            <div className="flex items-center justify-center gap-2 mt-3">
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-600 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Verified
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-600 flex items-center gap-1">
                <Star className="w-3 h-3" /> Premium
              </span>
            </div>
          </div>
        </div>

        {/* Info Cards */}
        <div className="col-span-2 space-y-4">
          {[
            { icon: Users, label: 'Полное имя', value: name },
            { icon: Globe, label: 'Email', value: data.email || 'Не указан' },
            { icon: Smartphone, label: 'Телефон', value: data.phone || 'Не указан' },
            { icon: Wallet, label: 'Баланс', value: `${fmt(total, 0)} ₽` },
            { icon: Shield, label: 'Безопасность', value: '2FA включена' },
          ].map((item, i) => (
            <div key={i} className="bg-white rounded-xl p-4 border border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-slate-500" />
                </div>
                <span className="text-slate-500">{item.label}</span>
              </div>
              <span className="font-medium text-slate-800">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <TopNav />
      <div className="flex flex-1">
        <Sidebar />
        {page === 'dashboard' && <DashboardPage />}
        {page === 'portfolio' && <PortfolioPage />}
        {page === 'history' && <HistoryPage />}
        {page === 'news' && <NewsPage />}
        {page === 'profile' && <ProfilePage />}
      </div>
    </div>
  );
}
